import {ReactNode} from 'react';

import {TooltipIcon} from 'notebook/tables/constants/TableColumnConstants';
import {TABLE_COLUMN, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {
  ColumnHeaderModel,
  createColumnHeaderModelSelector,
  makeStandaloneColumnHeaderModel,
} from 'notebook/tables/store/models/ColumnHeaderModel';
import {
  createRowHeaderModelSelector,
  makeStandaloneRowHeaderModel,
  RowHeaderModel,
} from 'notebook/tables/store/models/RowHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {TableEditableStore} from 'notebook/tables/store/TableStore';

/**
 * The model `TableAxisCell` renders from. `TableAxisCell` is one component rendered on both axes,
 * so it reads the model for whichever axis it is on — `ColumnHeaderModel` from `TableHeaderRow` and
 * `TableSingleInput`, `RowHeaderModel` from `TableRow` — and narrows with
 * {@link isColumnHeaderModel} for the column-only parts. Both extend `AxisHeaderModel`, which holds
 * what the two axes have in common.
 */
export type AxisCellModel = ColumnHeaderModel | RowHeaderModel;

/** Narrows an {@link AxisCellModel} to the column axis. */
export function isColumnHeaderModel(axisCellModel: AxisCellModel): axisCellModel is ColumnHeaderModel {
  return axisCellModel.dim === TABLE_COLUMN;
}

/**
 * Creates a selector for the axis cell at (`dim`, `index`) against the table store. Used for axis
 * cells rendered inside a table; standalone axis cells use {@link makeStandaloneAxisCellModel}.
 */
export function createAxisCellModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  dim: TableDimension,
  index: number
): (store: TableEditableStore) => AxisCellModel {
  return dim === TABLE_COLUMN
    ? createColumnHeaderModelSelector(tableSelector, index)
    : createRowHeaderModelSelector(tableSelector, index);
}

/**
 * An {@link AxisCellModel} for an axis cell rendered outside of any table store (for example
 * `SimpleTableAxisCell`), whose label and icons come directly from its props rather than a table.
 */
export function makeStandaloneAxisCellModel({
  dim,
  index,
  customLabel = null,
  label = null,
  tooltipIcons = null,
}: {
  dim: TableDimension;
  index: number;
  customLabel?: ReactNode;
  label?: string | null;
  tooltipIcons?: Array<TooltipIcon> | null;
}): AxisCellModel {
  return dim === TABLE_COLUMN
    ? makeStandaloneColumnHeaderModel({index, customLabel, label, tooltipIcons})
    : makeStandaloneRowHeaderModel({index, customLabel});
}
